import { AuthState } from '@okta/okta-auth-js';
import axios, { AxiosRequestConfig } from 'axios';
import BaseService from './baseService';
import { Api } from '../types/apiData';
import { IFilterOptions, IMappingFilterData } from '../types/mappingFilterData';
import { ApiDiffResponseDto } from '../types/apiDiff';

export default class ApiService extends BaseService {

    constructor() {
        super();
    }

    public async getApisByAreaId(id: string, authState: AuthState | null) {
        try {
            const response = await axios.get(`${this.REACT_APP_API_BASE_URL}/apis/area/${id}`, this.getHeaders(authState));
            // Transform response to an array if it's not already
            return Array.isArray(response.data) ? response.data : [response.data];
        } catch (error) {
            console.error("Error fetching API information:", error);
            throw error;
        }
    }
    public async getAllApis(authState: AuthState | null) {
        try {
            const response = await axios.get(`${this.REACT_APP_API_BASE_URL}/apis`, this.getHeaders(authState));
            return response.data;
        } catch (error) {
            console.error("Error fetching API information:", error);
            throw error;
        }
    }

    public async getFilterOptions(authState: AuthState | null) : Promise<IFilterOptions> {
        try {
            const response = await axios.get(`${this.REACT_APP_API_BASE_URL}/apis/filter-options`, this.getHeaders(authState));
            return response.data as IFilterOptions;
        } catch (error) {
            console.error("Error fetching API information:", error);
            throw error;
        }
    }


    // Get APIs by filter
    public async getApisByFilter(filter: IMappingFilterData, authState: AuthState | null) {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/apis/filter`, filter, this.getHeaders(authState));
            return response.data;
        } catch (error) {
            console.error("Error fetching API information:", error);
            throw error;
        }
    }

    public async getApiById(id: string, authState: AuthState | null) : Promise<Api> {
        try {
            const response = await axios.get(`${this.REACT_APP_API_BASE_URL}/api/${id}`, this.getHeaders(authState));
            if (response.status != 200) {
                throw new Error(`Error fetching API with ID ${id}: ${response.statusText}`);
            }
            return response.data as Api;
        } catch (error) {
            console.error("Error fetching API information:", error);
            throw error;
        }
    }

    public async addAPI(formData: any, authState: AuthState | null) {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api`, formData, this.getHeaders(authState));
            return response.data;
        } catch (error: any) {
            console.error("Error adding API:", error);
            throw error;
        }
    }

    public async addAttachments(formData: FormData, authState: AuthState | null) {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/attachments_upload`,
                formData,
                this.getHeadersMultipartFormData(authState)
            );
            return response.data;
        } catch (error: any) {
            console.error("Error uploading attachments:", error);
            throw error;
        }
    }

    public async addApiSpecification(formData: FormData, authState: AuthState | null) {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/api_specification_upload`,
                formData,
                this.getHeadersMultipartFormData(authState)
            );
            return response.data;
        } catch (error: any) {
            console.error("Error uploading API specification:", error);
            throw error;
        }
    }

    public async editAPI(id: string, formData: any, authState: AuthState | null) {
        try {
            const response = await axios.put(`${this.REACT_APP_API_BASE_URL}/api/${id}`, formData, this.getHeaders(authState));
            return response.data;
        } catch (error) {
            console.error("Error updating API:", error);
            throw error;
        }
    }

    public async deleteApiById(id: string, authState: AuthState | null) {
        try {
            const response = await axios.delete(`${this.REACT_APP_API_BASE_URL}/api/${id}`, this.getHeaders(authState));
            return response.data;
        } catch (error) {
            console.error("Error deleting API:", error);
            throw error;
        }
    }

    public async refreshApimGatewayUrls(id: string, authState: AuthState | null): Promise<Api> {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/api/${id}/apim-gateway-urls/refresh`,
                {},
                this.getHeaders(authState)
            );
            return response.data as Api;
        } catch (error) {
            console.error("Error refreshing APIM gateway URLs:", error);
            throw error;
        }
    }

    public async addAPIViaBatch(formData: FormData, authState: AuthState | null) {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/apis/upload-csv`,
                formData,
                this.getHeadersMultipartFormData(authState)
            );
            return { httpResponse: response };

        } catch (error: any) {
            // Improved Error Handling
            let errorMessage = "An unexpected error occurred."; // Generic message
            if (error.response) {
                errorMessage = `Message: ${
                    error.response.data.error || error.response.data.message + ' - ' + error.response.data.details
                }`;

                return { httpResponse: null, errorResponse: [errorMessage] };

            } else if (error.request) {
                // Request made but no response
                errorMessage = "No response from the server.";
                return { httpResponse: null, errorResponse: [errorMessage] };
            } else if (error.message === "Network Error") {
                errorMessage =
                "Network error. Please check your internet connection or if the server is running correctly.";
                return { httpResponse: null, errorResponse: [errorMessage] };
            }
        }
    }

    public async exportApisToCSV(apiIds: string[], authState: AuthState | null): Promise<Blob> {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/apis/export-csv`,
                { apiIds },
                {
                    ...this.getHeaders(authState),
                    responseType: 'blob' as const // Important for handling binary data
                }
            );
            return response.data as Blob;
        } catch (error) {
            console.error("Error exporting APIs to CSV:", error);
            throw error;
        }
    }

    /**
     * Create a new version of an existing API
     */
    public async createApiVersion(parentApiId: string, formData: any, authState: AuthState | null) {
        try {
            const response = await axios.post(
                `${this.REACT_APP_API_BASE_URL}/api/${parentApiId}/version`,
                formData,
                this.getHeaders(authState)
            );
            return response.data;
        } catch (error: any) {
            console.error("Error creating API version:", error);
            throw error;
        }
    }

    /**
     * Get all versions of an API
     */
    public async getApiVersions(parentApiId: string, authState: AuthState | null, signal?: AbortSignal): Promise<Api[]> {
        try {
            const config: AxiosRequestConfig = { ...this.getHeaders(authState), signal };
            const response = await axios.get(
                `${this.REACT_APP_API_BASE_URL}/api/${parentApiId}/versions`,
                config
            );
            return response.data as Api[];
        } catch (error) {
            console.error("Error fetching API versions:", error);
            throw error;
        }
    }

    /**
     * Compare two versions of an API (MSIAPI-1432). `fromVersion`/`toVersion` are version numbers (e.g. "1.0.0").
     */
    public async compareApiVersions(
        apiId: string,
        fromVersion: string,
        toVersion: string,
        authState: AuthState | null,
        signal?: AbortSignal
    ): Promise<ApiDiffResponseDto> {
        try {
            const config: AxiosRequestConfig = {
                ...this.getHeaders(authState),
                params: { from: fromVersion, to: toVersion },
                signal,
            };
            const response = await axios.get(
                `${this.REACT_APP_API_BASE_URL}/api/${apiId}/compare`,
                config
            );
            return response.data as ApiDiffResponseDto;
        } catch (error) {
            console.error("Error comparing API versions:", error);
            throw error;
        }
    }

}